import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, Text, Caption1, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  HomeIcon,
  SparklesIcon,
  RectangleStackIcon,
  CalendarIcon,
  LinkIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  SunIcon,
  MoonIcon,
} from '@heroicons/react/24/outline';
import WorkspaceSwitcher from '../WorkspaceSwitcher';
import CreateWorkspaceModal from '../modals/CreateWorkspaceModal';
import { LogoMark } from '../brand/Logo';

const useStyles = makeStyles({
  aside: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  logoBorder: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  logo: {
    color: tokens.colorBrandForeground1,
  },
  separator: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  navItem: {
    color: tokens.colorNeutralForeground1,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  navItemActive: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2Hover,
    },
  },
  rowButton: {
    color: tokens.colorNeutralForeground1,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  logoutButton: {
    color: tokens.colorNeutralForeground1,
    ':hover': {
      backgroundColor: tokens.colorStatusDangerBackground1,
      color: tokens.colorStatusDangerForeground1,
    },
  },
  email: {
    color: tokens.colorNeutralForeground2,
  },
});

const Sidebar = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const { currentUser, userProfile, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const navItems = [
    { name: t('navigation.dashboard'), path: '/home', icon: HomeIcon },
    { name: t('navigation.generate'), path: '/generate', icon: SparklesIcon },
    { name: t('navigation.library'), path: '/library', icon: RectangleStackIcon },
    { name: t('navigation.calendar'), path: '/calendar', icon: CalendarIcon },
    { name: t('navigation.platforms'), path: '/platforms', icon: LinkIcon },
    { name: t('navigation.analytics'), path: '/analytics', icon: ChartBarIcon },
    { name: t('navigation.settings'), path: '/settings', icon: Cog6ToothIcon },
  ];

  const displayName = userProfile?.displayName || currentUser?.displayName || 'User';

  return (
    <>
      {/* Sidebar */}
      <aside
        className={mergeClasses('w-64 flex flex-col flex-shrink-0', styles.aside)}
      >
        {/* Logo */}
        <div className={`h-16 flex items-center gap-2 px-6 ${styles.logoBorder}`}>
          <LogoMark size={28} />
          <Text as="h1" weight="bold" size={500} className={styles.logo}>RewordGenie</Text>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1">
          {/* Workspace Switcher */}
          <div className="mb-4">
            <WorkspaceSwitcher onCreateWorkspace={() => setIsCreateModalOpen(true)} />
          </div>

          {/* Separator Line */}
          <div className={`${styles.separator} my-4`} />

          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  mergeClasses(
                    'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors',
                    isActive ? styles.navItemActive : styles.navItem
                  )
                }
              >
                <Icon className="w-5 h-5" />
                {item.name}
              </NavLink>
            );
          })}
        </nav>

        {/* Theme Switcher */}
        <div className="px-4 pb-2">
          <button
            onClick={toggleTheme}
            className={mergeClasses(
              'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors',
              styles.rowButton
            )}
          >
            {theme === 'light' ? (
              <>
                <MoonIcon className="w-5 h-5" />
                <span>{t('theme.darkMode')}</span>
              </>
            ) : (
              <>
                <SunIcon className="w-5 h-5" />
                <span>{t('theme.lightMode')}</span>
              </>
            )}
          </button>
        </div>

        {/* Separator Line */}
        <div className={styles.separator} />

        {/* User Section */}
        <div className="p-4 space-y-2">
          <button
            onClick={() => navigate('/settings/profile')}
            className={mergeClasses(
              'w-full flex items-center gap-3 rounded-lg p-2 transition-colors',
              styles.rowButton
            )}
          >
            <Avatar
              name={displayName}
              image={userProfile?.photoURL ? { src: userProfile.photoURL } : undefined}
              color="brand"
              size={40}
            />
            <div className="flex-1 min-w-0 text-left">
              <Text as="p" size={200} weight="medium" truncate block>
                {displayName}
              </Text>
              <Caption1 as="p" truncate block className={styles.email}>
                {currentUser?.email || 'user@example.com'}
              </Caption1>
            </div>
          </button>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className={mergeClasses(
              'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors',
              styles.logoutButton
            )}
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5" />
            <span>{t('auth.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Create Workspace Modal */}
      <CreateWorkspaceModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </>
  );
};

export default Sidebar;
